import { useQuery } from "@tanstack/react-query";
import { fetchClaimsWithSteps } from "../api/claims";
import { fetchActiveSuppliers } from "../api/suppliers";
import { useFactory } from "@/core/hooks/useFactory";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  TrendingUp,
  Building2,
  Calendar
} from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart, Tooltip } from "recharts";
import { Link } from "react-router-dom";

const statusConfig = {
  open: { label: "Open", color: "var(--chart-1)", bgClass: "bg-warning/20 text-warning" },
  in_progress: { label: "In Progress", color: "var(--chart-2)", bgClass: "bg-chart-2/20 text-chart-2" },
  pending_supplier: { label: "Pending Supplier", color: "var(--chart-3)", bgClass: "bg-chart-3/20 text-chart-3" },
  pending_approval: { label: "Pending Approval", color: "var(--chart-5)", bgClass: "bg-chart-4/20 text-chart-4" },
  closed: { label: "Closed", color: "var(--chart-4)", bgClass: "bg-success/20 text-success" },
};

export function EightDDashboard() {
  const { selectedFactory } = useFactory();

  const { data: claims } = useQuery({
    queryKey: ["mod_8d_claims_dashboard", selectedFactory?.id],
    queryFn: () => fetchClaimsWithSteps(selectedFactory!.id),
    enabled: !!selectedFactory?.id,
  });

  const { data: suppliers } = useQuery({
    queryKey: ["mod_8d_suppliers_dashboard", selectedFactory?.id],
    queryFn: () => fetchActiveSuppliers(selectedFactory!.id),
    enabled: !!selectedFactory?.id,
  });

  // Calculate metrics
  const statusCounts = claims?.reduce((acc, claim) => {
    acc[claim.status] = (acc[claim.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const totalClaims = claims?.length || 0;
  const openClaims = (statusCounts.open || 0) + (statusCounts.in_progress || 0) + (statusCounts.pending_supplier || 0) + (statusCounts.pending_approval || 0);
  const closedClaims = statusCounts.closed || 0;
  const closureRate = totalClaims > 0 ? Math.round((closedClaims / totalClaims) * 100) : 0;

  // Claims this month
  const thisMonthStart = startOfMonth(new Date());
  const claimsThisMonth = claims?.filter(c => new Date(c.created_at) >= thisMonthStart).length || 0;

  // Average resolution time (for closed claims)
  const closedClaimsWithDates = claims?.filter(c => c.status === "closed") || [];
  const avgResolutionDays = closedClaimsWithDates.length > 0
    ? Math.round(
        closedClaimsWithDates.reduce((sum, c) => {
          const created = new Date(c.created_at);
          const updated = new Date(c.updated_at);
          return sum + (updated.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
        }, 0) / closedClaimsWithDates.length
      )
    : 0;

  // Pie chart data
  const pieData = Object.entries(statusCounts).map(([status, count]) => ({
    name: statusConfig[status as keyof typeof statusConfig]?.label || status,
    value: count,
    color: statusConfig[status as keyof typeof statusConfig]?.color || "var(--muted)",
  }));

  // Claims by supplier
  const supplierClaims = claims?.reduce((acc, claim) => {
    const supplierName = claim.supplier?.name || "Unknown";
    acc[supplierName] = (acc[supplierName] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const supplierData = Object.entries(supplierClaims)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name: name.length > 15 ? name.slice(0, 15) + "..." : name, claims: count }));

  // Trend data (last 7 days)
  const trendData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dateStr = format(date, "yyyy-MM-dd");
    const created = claims?.filter(c => format(new Date(c.created_at), "yyyy-MM-dd") === dateStr).length || 0;
    const closed = claims?.filter(c => 
      c.status === "closed" && 
      format(new Date(c.updated_at), "yyyy-MM-dd") === dateStr
    ).length || 0;
    return {
      date: format(date, "EEE"),
      created,
      closed,
    };
  });

  // Recent claims
  const recentClaims = claims?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-card to-card/50 border-border/50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground font-medium">Total Claims</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{totalClaims}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  +{claimsThisMonth} this month
                </p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card to-card/50 border-border/50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground font-medium">Open Claims</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{openClaims}</p>
                <p className="text-xs text-warning mt-1">
                  Requires attention
                </p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card to-card/50 border-border/50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground font-medium">Closure Rate</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{closureRate}%</p>
                <p className="text-xs text-success mt-1">
                  {closedClaims} resolved
                </p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card to-card/50 border-border/50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground font-medium">Avg Resolution</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{avgResolutionDays}d</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Days to close
                </p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-chart-2/10 flex items-center justify-center">
                <Clock className="h-5 w-5 md:h-6 md:w-6 text-chart-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Status Distribution */}
        <Card className="border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                No claims data
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {Object.entries(statusConfig).map(([key, config]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-muted-foreground">{config.label}</span>
                  <span className="font-medium">{statusCounts[key] || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Claims Trend */}
        <Card className="border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              7-Day Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorClosed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--chart-4)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--chart-4)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="var(--primary)"
                    fillOpacity={1}
                    fill="url(#colorCreated)"
                    strokeWidth={2}
                    name="Created"
                  />
                  <Area
                    type="monotone"
                    dataKey="closed"
                    stroke="var(--chart-4)"
                    fillOpacity={1}
                    fill="url(#colorClosed)"
                    strokeWidth={2}
                    name="Closed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Suppliers */}
        <Card className="border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              Claims by Supplier
            </CardTitle>
          </CardHeader>
          <CardContent>
            {supplierData.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={supplierData} layout="vertical">
                    <XAxis 
                      type="number" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                      width={80}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: '8px',
                        fontSize: '12px'
                      }} 
                    />
                    <Bar 
                      dataKey="claims" 
                      fill="var(--primary)"
                      radius={[0, 4, 4, 0]}
                      name="Claims"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                No supplier data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            Recent Claims
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentClaims.length > 0 ? (
            <div className="space-y-3">
              {recentClaims.map((claim) => {
                const config = statusConfig[claim.status as keyof typeof statusConfig];
                const completedSteps = claim.steps?.filter((s: any) => s.status === "completed").length || 0;
                const totalSteps = 8;
                
                return (
                  <Link
                    key={claim.id}
                    to={`/8d-management?tab=claims&claim=${claim.id}`}
                    className="block"
                  >
                    <div
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0">
                          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-primary" />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{claim.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {claim.claim_number} • {claim.supplier?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="hidden sm:block text-right">
                          <p className="text-xs text-muted-foreground">
                            Step {completedSteps}/{totalSteps}
                          </p>
                          <div className="w-16 h-1.5 bg-muted rounded-full mt-1">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                            />
                          </div>
                        </div>
                        <Badge variant="outline" className={config?.bgClass}>
                          {config?.label || claim.status}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No recent claims
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
